import React, { useRef, useLayoutEffect } from 'react';
import { SectionInstance } from './types';
import { GripVertical, Copy, Trash2, ChevronUp, ChevronDown, RefreshCw } from 'lucide-react';
import { InlineEditContext } from './InlineEditContext';

interface Props {
  section: SectionInstance;
  index: number;
  total: number;
  isSelected: boolean;
  onSelect: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onRegenerate: () => void;
  onControlChange?: (sectionId: string, key: string, value: string) => void;
  dragHandleProps?: Record<string, any>;
  children: React.ReactNode;
  /** project_id of the site — passed through to InlineEditContext for data-scoped components */
  projectId?: string;
}

// Size multiplier map — keyed by the select value stored in controls
const SIZE_MULT: Record<string, number> = { sm: 0.8, md: 1, lg: 1.25, xl: 1.5 };

const SectionWrapper: React.FC<Props> = ({
  section, index, total, isSelected, onSelect,
  onMoveUp, onMoveDown, onDuplicate, onDelete, onRegenerate,
  onControlChange, dragHandleProps, projectId,
  children,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  // Tracks whether we injected a bg override so we only clear it when WE set it,
  // never when the renderer owns its own inline background (hero gradient, etc.)
  const injectedBg = useRef(false);

  useLayoutEffect(() => {
    const el = contentRef.current?.firstElementChild as HTMLElement | null;
    if (!el) return;
    const mode = section.controls.section_bg_mode || '';
    if (mode === 'solid' && section.controls.section_bg_color) {
      el.style.background = section.controls.section_bg_color;
      injectedBg.current = true;
    } else if (mode === 'gradient') {
      const gFrom = section.controls.section_gradient_from || 'var(--brand-primary, #1a365d)';
      const gTo   = section.controls.section_gradient_to   || 'var(--brand-secondary, #2563eb)';
      const gDir  = section.controls.section_gradient_direction || '135deg';
      el.style.background = `linear-gradient(${gDir}, ${gFrom} 0%, ${gTo} 100%)`;
      injectedBg.current = true;
    } else if (injectedBg.current) {
      el.style.background = '';
      injectedBg.current = false;
    }
  });

  // Build scoped CSS for text colour + size overrides
  const scopeClass = `sb-s-${section.section_id.replace(/-/g, '')}`;
  const textColor = section.controls.section_text_color || '';
  const sizeMult = SIZE_MULT[section.controls.section_text_size] ?? null;
  const styleRules: string[] = [];
  if (textColor) {
    styleRules.push(
      `.${scopeClass} h1,.${scopeClass} h2,.${scopeClass} h3,.${scopeClass} h4,` +
      `.${scopeClass} p,.${scopeClass} li,.${scopeClass} span,.${scopeClass} a` +
      `{color:${textColor}!important}`
    );
  }
  if (sizeMult && sizeMult !== 1) {
    styleRules.push(`.${scopeClass} h1{font-size:calc(2.5rem*${sizeMult})!important}`);
    styleRules.push(`.${scopeClass} h2{font-size:calc(2rem*${sizeMult})!important}`);
    styleRules.push(`.${scopeClass} h3{font-size:calc(1.5rem*${sizeMult})!important}`);
    styleRules.push(`.${scopeClass} h4{font-size:calc(1.25rem*${sizeMult})!important}`);
    styleRules.push(`.${scopeClass} p,.${scopeClass} li{font-size:calc(1rem*${sizeMult})!important}`);
  }
  // Button colour overrides — targets .sb-cta-btn class on CTA anchors/buttons in renderers
  const btnBg = section.controls.section_button_color || '';
  const btnText = section.controls.section_button_text_color || '';
  if (btnBg) styleRules.push(`.${scopeClass} .sb-cta-btn{background:${btnBg}!important;border-color:${btnBg}!important}`);
  if (btnText) styleRules.push(`.${scopeClass} .sb-cta-btn{color:${btnText}!important}`);
  const scopedCss = styleRules.join('\n');

  return (
    <InlineEditContext.Provider
      value={{
        sectionId: section.section_id,
        componentId: section.component_id,
        controls: section.controls ?? {},
        onControlChange: onControlChange ?? (() => {}),
        isEditMode: isSelected,
        projectId,
      }}
    >
      <div
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
        style={{
          position: 'relative',
          outline: isSelected ? '2px solid var(--brand-primary, #2563eb)' : '2px solid transparent',
          outlineOffset: -2,
          cursor: 'pointer',
          transition: 'outline-color 0.15s',
        }}
      >
        {/* Toolbar — visible when selected */}
        {isSelected && (
          <div style={{
            position: 'absolute', top: -32, right: 8, zIndex: 10,
            display: 'flex', gap: 2, background: '#1e293b', borderRadius: 6,
            padding: '4px 6px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}>
            <span {...(dragHandleProps ?? {})} title="Drag to reorder" style={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center', padding: 4, color: '#fff' }}>
              <GripVertical size={14} />
            </span>
            <ToolbarBtn icon={<ChevronUp size={14} />} title="Move up" onClick={onMoveUp} disabled={index === 0} />
            <ToolbarBtn icon={<ChevronDown size={14} />} title="Move down" onClick={onMoveDown} disabled={index === total - 1} />
            <ToolbarBtn icon={<Copy size={14} />} title="Duplicate" onClick={onDuplicate} />
            <ToolbarBtn icon={<RefreshCw size={14} />} title="Regenerate with AI" onClick={onRegenerate} />
            <ToolbarBtn icon={<Trash2 size={14} />} title="Delete" onClick={onDelete} danger />
          </div>
        )}

        {/* Component label */}
        {isSelected && (
          <div style={{
            position: 'absolute', top: 4, left: 4, zIndex: 10,
            padding: '2px 8px', background: 'rgba(37,99,235,0.9)',
            color: '#fff', fontSize: '0.7rem', borderRadius: 4,
            pointerEvents: 'none',
          }}>
            {section.component_id}
          </div>
        )}

        {scopedCss && <style dangerouslySetInnerHTML={{ __html: scopedCss }} />}
        <div ref={contentRef} className={scopeClass}>{children}</div>
      </div>
    </InlineEditContext.Provider>
  );
};

const ToolbarBtn: React.FC<{
  icon: React.ReactNode;
  title: string;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
}> = ({ icon, title, onClick, disabled, danger }) => (
  <button
    onClick={e => { e.stopPropagation(); onClick?.(); }}
    disabled={disabled}
    title={title}
    style={{
      border: 'none', background: 'none', cursor: disabled ? 'default' : 'pointer',
      padding: 4, color: danger ? '#ef4444' : disabled ? '#64748b' : '#fff',
      opacity: disabled ? 0.4 : 1,
    }}
  >
    {icon}
  </button>
);

export default SectionWrapper;
